import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Modal, IconButton, Divider, ButtonGroup, Message } from "rsuite";
import CloseIcon from "@rsuite/icons/Close";
import ExportIcon from "@rsuite/icons/Export";
import ImageIcon from "@rsuite/icons/Image";
import ReloadIcon from "@rsuite/icons/Reload";
import { colors } from "../../../../ui";
import ChartRenderer from "./ChartRenderer";
import ChartControls from "./ChartControls";

const FullscreenModal = ({
  open,
  onClose,
  chartData,
  canvasConfig,
  fusionConfig,
  chartLibrary,
  configControls,
  onChartLibraryChange,
  onTimeRangeChange,
  timeRange,
  metadata,
  darkMode,
  availableUnitTypes,
  onUnitChange,
  className,
  ...props
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [open, onClose]);

  // Export chart as image using html2canvas approach
  const handleExportChart = async (format = "png") => {
    if (!chartContainerRef.current) return;

    setIsExporting(true);
    setExportError(null);

    try {
      // Use html2canvas for reliable export
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartContainerRef.current, {
        backgroundColor: darkMode ? "#2c3e50" : "#ffffff",
        scale: 2, // Higher quality
        logging: false,
      });

      // Convert to desired format and download
      const fileName = `chart_${metadata?.sme || "system"}_${new Date().toISOString().split("T")[0]}`;

      if (format === "png" || format === "jpg") {
        const mimeType = format === "png" ? "image/png" : "image/jpeg";
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${fileName}.${format}`;
          link.click();
          URL.revokeObjectURL(url);
        }, mimeType);
      } else if (format === "pdf") {
        // For PDF, we need jsPDF
        const jsPDF = (await import('jspdf')).default;
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height]
        });
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save(`${fileName}.pdf`);
      } else if (format === "svg") {
        setExportError("SVG export is not supported. Please use PNG or PDF.");
        setIsExporting(false);
        return;
      }
    } catch (error) {
      console.error("Export failed:", error);
      setExportError("Failed to export chart. Please try again.");
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setExportError(null);
      }, 2000);
    }
  };

  // Export chart data as CSV
  const handleExportData = () => {
    if (!chartData || chartData.length === 0) return;

    try {
      // Build CSV content
      let csvContent = "";

      // Create header row with timestamp and all series names
      const headers = ["Timestamp"];
      chartData.forEach((series) => {
        headers.push(series.name || series.key || "Series");
      });
      csvContent += headers.join(",") + "\n";

      // Get all unique timestamps across all series
      const timestampSet = new Set();
      chartData.forEach((series) => {
        // Check both dataPoints and data properties
        const points = series.dataPoints || series.data;
        if (points && Array.isArray(points)) {
          points.forEach((point) => {
            const timestamp = point.x || point.date || point.time || point[0];
            if (timestamp) {
              timestampSet.add(new Date(timestamp).getTime());
            }
          });
        }
      });

      const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

      // Build data rows
      timestamps.forEach((timestamp) => {
        const row = [new Date(timestamp).toISOString()];

        chartData.forEach((series) => {
          const points = series.dataPoints || series.data;
          const dataPoint = points?.find((point) => {
            const pointTime = point.x || point.date || point.time || point[0];
            return new Date(pointTime).getTime() === timestamp;
          });

          const value = dataPoint ? (dataPoint.y || dataPoint.value || dataPoint[1]) : "";
          row.push(value);
        });

        csvContent += row.join(",") + "\n";
      });

      // Create download link
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `chart_data_${metadata?.sme || "system"}_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV export failed:", error);
      setExportError("Failed to export data as CSV. Please try again.");
    }
  };

  const exportOptions = [
    { key: "png", label: "PNG Image", icon: <ImageIcon /> },
    { key: "jpg", label: "JPEG Image", icon: <ImageIcon /> },
    { key: "pdf", label: "PDF Document", icon: <ExportIcon /> },
  ];

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      size="full"
      backdrop="static"
      keyboard={false}
      className={className}
      {...props}
    >
      <ModalContent>
        {/* Header */}
        <FullscreenHeader>
          <HeaderLeft>
            <ModalTitle>
              Chart Visualization
              {metadata?.sme && <SystemName>- {metadata.sme}</SystemName>}
            </ModalTitle>
            <ChartInfo>
              {chartData.length} data series • {timeRange.toUpperCase()} •{" "}
              {chartLibrary === "canvas" ? "CanvasJS" : "FusionCharts"}
            </ChartInfo>
          </HeaderLeft>

          <HeaderRight>
            <ChartControls
              timeRange={timeRange}
              onTimeRangeChange={onTimeRangeChange}
              chartLibrary={chartLibrary}
              onChartLibraryChange={onChartLibraryChange}
              configControls={configControls}
              showSelector={false}
              availableUnitTypes={availableUnitTypes}
              onUnitChange={onUnitChange}
            />

            <ButtonGroup size="sm">
              <IconButton
                icon={<ReloadIcon />}
                onClick={() => window.location.reload()}
                title="Refresh data"
                appearance="subtle"
              />
              <IconButton
                icon={<ExportIcon />}
                onClick={() => handleExportChart("png")}
                loading={isExporting}
                title="Export as PNG"
                appearance="subtle"
              />
              <IconButton
                icon={<CloseIcon />}
                onClick={onClose}
                title="Close fullscreen (ESC)"
                appearance="subtle"
              />
            </ButtonGroup>
          </HeaderRight>
        </FullscreenHeader>

        <Divider style={{ margin: 0 }} />

        {/* Export Error */}
        {exportError && (
          <ErrorContainer>
            <Message type="error" closable onClose={() => setExportError(null)}>
              {exportError}
            </Message>
          </ErrorContainer>
        )}

        {/* Chart Content */}
        <ChartSection>
          {chartData.length > 0 ? (
            <FullscreenChartContainer ref={chartContainerRef}>
              <ChartRenderer
                chartData={chartData}
                canvasConfig={canvasConfig}
                fusionConfig={fusionConfig}
                chartLibrary={chartLibrary}
                darkMode={darkMode}
                height="100%"
              />
            </FullscreenChartContainer>
          ) : (
            <EmptyState>
              <EmptyIcon>📈</EmptyIcon>
              <EmptyTitle>No Data to Display</EmptyTitle>
              <EmptyDescription>
                Select data points from the main chart interface to view them in
                fullscreen mode.
              </EmptyDescription>
            </EmptyState>
          )}
        </ChartSection>

        {/* Footer with additional controls */}
        <FullscreenFooter>
          <FooterLeft>
            <ExportOptions>
              <ExportLabel>Export as:</ExportLabel>
              {exportOptions.map((option) => (
                <ExportButton
                  key={option.key}
                  onClick={() => handleExportChart(option.key)}
                  disabled={isExporting || chartData.length === 0}
                  title={`Export chart as ${option.label}`}
                >
                  {option.icon}
                  {option.label}
                </ExportButton>
              ))}
            </ExportOptions>
          </FooterLeft>

          <FooterRight>
            <SaveConfigButton
              onClick={handleExportData}
              disabled={chartData.length === 0}
              title="Export chart data as CSV file"
            >
              <ExportIcon style={{ marginRight: "6px" }} />
              Export Data (CSV)
            </SaveConfigButton>
          </FooterRight>
        </FullscreenFooter>
      </ModalContent>
    </StyledModal>
  );
};

// Styled Components
const StyledModal = styled(Modal)`
  .rs-modal-dialog {
    width: 100vw !important;
    height: 100vh !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    margin: 0 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .rs-modal-body {
    padding: 0 !important;
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  .rs-modal-header,
  .rs-modal-footer {
    display: none !important;
  }
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: white;
  overflow: hidden;
`;

const FullscreenHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SystemName = styled.span`
  color: ${colors.neutral[600]};
  font-weight: normal;
`;

const ChartInfo = styled.div`
  color: ${colors.neutral[600]};
  font-size: 13px;
`;

const ErrorContainer = styled.div`
  padding: 12px 24px;
  background: ${colors.error.light};
`;

const ChartSection = styled.div`
  flex: 1;
  padding: 20px;
  overflow: hidden;
  display: flex;
  align-items: stretch;
  justify-content: center;
  min-height: 0;
`;

const FullscreenChartContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  min-height: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${colors.neutral[600]};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  margin: 0 0 8px 0;
  color: ${colors.neutral[700]};
  font-size: 20px;
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 14px;
  max-width: 400px;
  margin: 0 auto;
  line-height: 1.5;
`;

const FullscreenFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: ${colors.neutral[50]};
  border-top: 1px solid ${colors.neutral[200]};
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExportOptions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const ExportLabel = styled.span`
  font-size: 13px;
  color: ${colors.neutral[600]};
  font-weight: 500;
`;

const ExportButton = styled.button`
  background: none;
  border: 1px solid ${colors.neutral[300]};
  color: ${colors.neutral[700]};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${colors.primary.main};
    color: ${colors.primary.main};
    background: ${colors.primary.main}10;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SaveConfigButton = styled.button`
  background: ${colors.primary.main};
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export default FullscreenModal;
